import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Paper,
  Stack,
  Title,
  Text,
  TextInput,
  NumberInput,
  Input,
  Select,
  MultiSelect,
  Avatar,
  Grid,
  Group,
  Button,
  Collapse,
  Divider,
  Tooltip,
  ActionIcon,
  Loader,
  Alert,
  UnstyledButton,
  Badge,
  Box,
  Modal,
  Switch,
} from "@mantine/core";
import { RichTextEditor, Link } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  IconPencil,
  IconAlertCircle,
  IconTrash,
  IconPlus,
  IconChevronRight,
  IconChevronDown,
  IconArrowLeft,
  IconRefresh,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import PageContent from "../shared/PageContent/PageContent";
import InfoVerificationPage from "../Verification/InfoVerificationPage";
import WelcomeSurveyEmailPreview from "./WelcomeSurveyEmailPreview";
import { buildPreviewData } from "./surveyPreviewData";
import "./WelcomeSurveySettingsPage.css";

const EMPTY_SETTINGS = {
  inboundPhone: "",
  contractText: "",
  termsText: "",
  emailHeadline: "",
  emailBody: "",
  headerImageUrl: "",
  emailHeroImageUrl: "",
  branchEmail: "",
  branchName: "",
  branchPhone: "",
  branchAddress: "",
  managerName: "",
  managerPhotoUrl: "",
};

// Counts the rep sync endpoint reports back, in the order they're shown.
const REP_SYNC_ROWS = [
  { key: "employees_found", label: "Employees found" },
  { key: "reps_created", label: "Reps created" },
  { key: "reps_existing", label: "Reps already linked" },
  { key: "office_links_created", label: "Office links created" },
];

const EMPTY_REP = {
  firstName: "",
  lastName: "",
  employeeId: "",
  phone: "",
  // fieldroutes_offices ids the rep is assigned to.
  officeIds: [],
};

// Reps arrive from FieldRoutes with the name split across two columns.
function repName(entry) {
  return `${entry?.first_name || ""} ${entry?.last_name || ""}`.trim();
}

const EMPTY_OFFICE = {
  name: "",
  frOfficeId: "",
  subdomain: "",
  authKey: "",
  authToken: "",
};

// Map the API's welcome survey settings payload onto the form's state shape.
function mapSettings(data) {
  if (!data) return EMPTY_SETTINGS;
  return {
    inboundPhone: data.welcome_survey_inbound_phone ?? "",
    contractText: data.welcome_survey_contract_text ?? "",
    // Not editable here, but the live preview renders it on the Terms step.
    termsText: data.welcome_survey_terms_text ?? "",
    // Hero copy on the invitation email; blank means the template's defaults.
    emailHeadline: data.welcome_survey_email_headline ?? "",
    emailBody: data.welcome_survey_email_body ?? "",
    headerImageUrl: data.welcome_survey_header_image_url ?? "",
    emailHeroImageUrl: data.welcome_survey_email_hero_image_url ?? "",
    branchEmail: data.welcome_survey_branch_email ?? "",
    branchName: data.welcome_survey_branch_name ?? "",
    branchPhone: data.welcome_survey_branch_phone ?? "",
    branchAddress: data.welcome_survey_branch_address ?? "",
    managerName: data.welcome_survey_manager_name ?? "",
    managerPhotoUrl: data.welcome_survey_manager_photo_url ?? "",
  };
}

// Each editable image has its own upload endpoint and the response field that
// carries the resulting URL back onto the form state.
const IMAGE_UPLOADS = {
  headerImageUrl: {
    endpoint: "/v1/welcome-survey/image/header",
    responseKey: "header_image_url",
    removeFlag: "remove_header_image",
  },
  emailHeroImageUrl: {
    endpoint: "/v1/welcome-survey/image/email-hero",
    responseKey: "email_hero_image_url",
    removeFlag: "remove_main_image",
  },
  managerPhotoUrl: {
    endpoint: "/v1/welcome-survey/image/manager-photo",
    responseKey: "manager_photo_url",
    removeFlag: "remove_manager_image",
  },
};

// Pull the image URL out of an upload response, tolerating different shapes:
// the specific settings key, common url-ish keys, or the first URL-looking
// string value in the object.
function pickImageUrl(obj, responseKey) {
  if (!obj || typeof obj !== "object") return "";
  const candidates = [
    responseKey,
    "url",
    "image_url",
    "imageUrl",
    "location",
    "Location",
  ];
  for (const key of candidates) {
    if (typeof obj[key] === "string" && obj[key]) return obj[key];
  }
  const found = Object.values(obj).find(
    value => typeof value === "string" && /^https?:\/\//i.test(value)
  );
  return found || "";
}

// Parse the settings response's `pest_coverage` map (keyed by fr_service_id)
// into editable service rows, plus the insect/service objects it references so
// labels and images can render without the bug-settings option catalog.
function parsePestCoverage(pestCoverage) {
  const serviceBugs = [];
  const insectsById = {};
  const servicesById = {};

  if (pestCoverage && typeof pestCoverage === "object") {
    Object.entries(pestCoverage).forEach(([serviceId, records]) => {
      if (!Array.isArray(records)) return;
      const includedBugs = [];
      const excludedBugs = [];
      let contractText = "";

      records.forEach(record => {
        const insectValue = String(record.fr_insect_id);
        if (record.coverage_type === "not_included") {
          excludedBugs.push(insectValue);
        } else {
          includedBugs.push(insectValue);
        }
        // Per-service contract terms ride along on the coverage records.
        if (!contractText && record.contract_terms) {
          contractText = record.contract_terms;
        }
        insectsById[insectValue] = {
          fr_insect_id: record.fr_insect_id,
          name: record.pest_name,
          image_url: record.image_url,
        };
        if (record.fr_service_id != null) {
          servicesById[String(record.fr_service_id)] = {
            fr_service_id: record.fr_service_id,
            name: record.service_type,
          };
        }
      });

      serviceBugs.push({
        serviceId: String(serviceId),
        includedBugs,
        excludedBugs,
        contractText,
      });
    });
  }

  return {
    serviceBugs,
    insects: Object.values(insectsById),
    services: Object.values(servicesById),
  };
}

// Parse the settings response's `services` array into editable service rows,
// plus the insect/service objects referenced so labels/images render before
// the bug-settings option catalog loads.
function parseServices(services) {
  const serviceBugs = [];
  const insectsById = {};
  const servicesById = {};

  if (Array.isArray(services)) {
    services.forEach(service => {
      const serviceId = String(service.fr_service_id);

      // Record an insect referenced by a service and return its string id.
      const collect = record => {
        const insectValue = String(record.fr_insect_id);
        insectsById[insectValue] = {
          fr_insect_id: record.fr_insect_id,
          name: record.name,
          image_url: record.image_url,
        };
        return insectValue;
      };

      const includedBugs = (
        Array.isArray(service.included) ? service.included : []
      ).map(collect);
      const excludedBugs = (
        Array.isArray(service.not_included) ? service.not_included : []
      ).map(collect);

      if (service.fr_service_id != null) {
        servicesById[serviceId] = {
          fr_service_id: service.fr_service_id,
          name: service.fr_service_type,
        };
      }

      serviceBugs.push({
        serviceId,
        includedBugs,
        excludedBugs,
        cancellationFee: service.cancellation_fee ?? "",
      });
    });
  }

  return {
    serviceBugs,
    insects: Object.values(insectsById),
    services: Object.values(servicesById),
  };
}

// Map the Service Bug Settings rows onto the save payload. Each service carries
// its id, name, and the full insect objects for included / not-included bugs.
function toBugPayload(serviceBugs, insectByValue, serviceNameByValue) {
  return serviceBugs
    .filter(entry => entry.serviceId)
    .map(entry => ({
      fr_service_id: Number(entry.serviceId),
      name: serviceNameByValue[entry.serviceId] || "",
      cancellation_fee:
        entry.cancellationFee === "" || entry.cancellationFee == null
          ? null
          : Number(entry.cancellationFee),
      included: entry.includedBugs.map(v => insectByValue[v]).filter(Boolean),
      not_included: entry.excludedBugs
        .map(v => insectByValue[v])
        .filter(Boolean),
    }));
}

// Month labels for the seasonal-window pickers. Values are the 1-based month
// numbers the API stores, as strings for Mantine's Select.
const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const MONTH_OPTIONS = MONTH_LABELS.map((label, index) => ({
  value: String(index + 1),
  label,
}));

// Target pests are picked by FieldRoutes insect id. Entries saved without one
// (hand-authored rows) are keyed by name behind this prefix so they still show
// up selected and survive a round trip instead of being silently dropped.
const NAMED_PEST_PREFIX = "name:";

// The live preview renders an iPhone 16 at its real point size — a 393 x 852pt
// screen inside a 12px bezel — so the survey lays out exactly as it does on the
// phone. The whole shell is then scaled to fit the panel; scaling changes how
// big it looks, not how anything inside it wraps.
const PHONE_OUTER_WIDTH = 417;
const PHONE_OUTER_HEIGHT = 876;
// Matches .survey-live-preview's sticky offset, so the fit is measured against
// where the panel sits once it's pinned.
const PHONE_STICKY_TOP = 16;
const PHONE_MIN_SCALE = 0.5;

// The live preview's service picker is keyed by FieldRoutes service id. A
// service type that only has seasonal windows configured has no id to key on
// (windows are stored by name), so those entries sit behind this prefix.
const SEASON_ONLY_PREFIX = "season:";

// Parse GET /service-seasons — a map of FieldRoutes service id to its windows —
// into editable rows, plus the insects referenced so labels and images render
// before the bug-settings catalog loads. The payload carries no service-type
// name, so `serviceType` is left blank here and resolved from the catalog
// (`serviceNameByValue[serviceId]`) wherever a row is displayed or saved.
function parseServiceSeasons(data) {
  const rows = [];
  const insectsById = {};

  if (data && typeof data === "object") {
    Object.entries(data).forEach(([serviceIdKey, seasons]) => {
      if (!Array.isArray(seasons)) return;
      // The service id every window in this group was saved under. The map is
      // keyed by it, and each window echoes it as `fr_service_id`; prefer the
      // window field, falling back to the key.
      const frServiceId =
        seasons.find(season => season.fr_service_id != null)?.fr_service_id ??
        serviceIdKey;
      rows.push({
        serviceType: "",
        serviceId: frServiceId != null ? String(frServiceId) : null,
        seasons: seasons.map(season => ({
          name: season.name ?? "",
          startMonth:
            season.start_month != null ? String(season.start_month) : null,
          endMonth: season.end_month != null ? String(season.end_month) : null,
          pests: (Array.isArray(season.target_pests)
            ? season.target_pests
            : []
          ).map(pest => {
            if (pest.fr_insect_id == null) {
              return `${NAMED_PEST_PREFIX}${pest.name}`;
            }
            const value = String(pest.fr_insect_id);
            insectsById[value] = {
              fr_insect_id: pest.fr_insect_id,
              name: pest.name,
              image_url: pest.image_url,
            };
            return value;
          }),
        })),
      });
    });
  }

  return { rows, insects: Object.values(insectsById) };
}

// Map one editable service-season row onto the PUT payload. The endpoint
// replaces that service type's whole window list, so every window goes up,
// keyed on the FieldRoutes service id. Rows carry the id they were configured
// under; windows saved before it was stored fall back to `serviceIdByName`,
// which resolves the service-type name to its numeric id.
function toSeasonPayload(row, insectByValue, serviceIdByName) {
  const serviceType = row.serviceType.trim();
  const frServiceId =
    row.serviceId ?? serviceIdByName?.[serviceType.toLowerCase()];
  return {
    fr_service_id: Number(frServiceId),
    service_type: serviceType,
    seasons: row.seasons.map(season => ({
      name: season.name.trim(),
      start_month: Number(season.startMonth),
      end_month: Number(season.endMonth),
      pests: season.pests
        .map(value => {
          if (value.startsWith(NAMED_PEST_PREFIX)) {
            return { pest_name: value.slice(NAMED_PEST_PREFIX.length) };
          }
          const insect = insectByValue[value];
          if (!insect) return null;
          return {
            pest_name: insect.name,
            fr_insect_id: insect.fr_insect_id,
            ...(insect.image_url ? { image_url: insect.image_url } : {}),
          };
        })
        .filter(Boolean),
    })),
  };
}

// Everything wrong with a service's visit windows, as sentences the admin can
// act on. Empty means the windows are safe to save. A service with no windows
// at all is fine — it just has no seasonal labels on its schedule.
function validateSeasonRows(rows) {
  const problems = [];

  rows.forEach(row => {
    const label = row.serviceType?.trim() || "This service";

    row.seasons.forEach((season, seasonIndex) => {
      const windowLabel = season.name?.trim() || `window ${seasonIndex + 1}`;
      if (!season.name?.trim()) {
        problems.push(`${label}: ${windowLabel} needs a visit name.`);
      }
      if (!season.startMonth || !season.endMonth) {
        problems.push(`${label}: ${windowLabel} needs a start and end month.`);
      }
    });
  });

  return [...new Set(problems)];
}

// Basic WYSIWYG editor for the email contract text. Stores its value as HTML.
// Shows a formatting toolbar while editable and renders read-only otherwise.
function ContractTextEditor({ label, value, editable, onChange }) {
  const editor = useEditor({
    extensions: [StarterKit, Link],
    content: value || "",
    editable,
    onUpdate: ({ editor: instance }) => onChange(instance.getHTML()),
  });

  // Keep the editor's editable state in sync with edit mode.
  useEffect(() => {
    editor?.setEditable(editable);
  }, [editable, editor]);

  // Reflect external value changes (initial load, cancel/revert) without
  // clobbering in-progress typing. Passing `false` skips firing onUpdate.
  useEffect(() => {
    if (!editor) return;
    const next = value || "";
    if (next !== editor.getHTML()) {
      editor.commands.setContent(next, false);
    }
  }, [value, editor]);

  return (
    <Input.Wrapper label={label}>
      <RichTextEditor editor={editor} mt={4}>
        {editable && (
          <RichTextEditor.Toolbar sticky>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Bold />
              <RichTextEditor.Italic />
              <RichTextEditor.Strikethrough />
              <RichTextEditor.ClearFormatting />
            </RichTextEditor.ControlsGroup>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.H1 />
              <RichTextEditor.H2 />
              <RichTextEditor.H3 />
            </RichTextEditor.ControlsGroup>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.BulletList />
              <RichTextEditor.OrderedList />
              <RichTextEditor.Blockquote />
            </RichTextEditor.ControlsGroup>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Link />
              <RichTextEditor.Unlink />
            </RichTextEditor.ControlsGroup>
          </RichTextEditor.Toolbar>
        )}
        <RichTextEditor.Content />
      </RichTextEditor>
    </Input.Wrapper>
  );
}

function WelcomeSurveySettingsPage() {
  const { getVerifiedJWT, user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const [error, setError] = useState(null);
  // Email content section: collapsed by default, and edited straight off the
  // rendered email — a pencil per part, each saving on its own.
  const [emailSectionOpen, setEmailSectionOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(false);
  const [branchDraft, setBranchDraft] = useState({
    branchName: "",
    branchAddress: "",
    branchPhone: "",
    branchEmail: "",
  });
  const [editingManager, setEditingManager] = useState(false);
  const [managerDraft, setManagerDraft] = useState("");
  const [editingHero, setEditingHero] = useState(false);
  const [heroDraft, setHeroDraft] = useState({ headline: "", body: "" });
  const [savingEmail, setSavingEmail] = useState(false);
  // Contract terms section (its own edit mode / save call).
  const [isEditingContract, setIsEditingContract] = useState(false);
  const [savingContract, setSavingContract] = useState(false);
  // Snapshot of the last-saved contract text, used to revert on cancel.
  const [contractSnapshot, setContractSnapshot] = useState("");
  const [isEditingBugs, setIsEditingBugs] = useState(false);
  const [bugSettings, setBugSettings] = useState(null);
  const [bugSettingsLoading, setBugSettingsLoading] = useState(false);
  // Each entry: { serviceId, includedBugs: string[], excludedBugs: string[] }.
  const [serviceBugs, setServiceBugs] = useState([]);
  // Snapshot of the last-saved rows, used to revert on cancel.
  const [serviceBugsSnapshot, setServiceBugsSnapshot] = useState([]);
  // Insect/service objects referenced by the saved pest coverage, so rows can
  // render labels/images before the bug-settings option catalog is loaded.
  const [coverageInsects, setCoverageInsects] = useState([]);
  const [coverageServices, setCoverageServices] = useState([]);
  // Whether the bug settings were changed since entering bug edit mode.
  const [bugsDirty, setBugsDirty] = useState(false);
  // Inline save error for bug settings: { message, conflicts: [{ service_type, bugs }] }.
  const [bugError, setBugError] = useState(null);
  // Index into serviceBugs of the service type being viewed, or null for the
  // list.
  const [selectedServiceIndex, setSelectedServiceIndex] = useState(null);
  // Live preview panel: the service type it renders (a service id, or a
  // SEASON_ONLY_PREFIX name), and a counter bumped to remount the survey so it
  // starts over with the boxes clear.
  const [previewServiceValue, setPreviewServiceValue] = useState(null);
  const [previewRunId, setPreviewRunId] = useState(0);
  // How far the device mock is scaled down to fit the panel (1 = life size).
  const [phoneScale, setPhoneScale] = useState(1);
  const phoneStageRef = useRef(null);
  // Accent color for the preview. It lives on the company row rather than
  // company_configs, so it comes from the branding endpoint.
  const [branding, setBranding] = useState(null);
  // Visit windows (named month ranges + target bugs), edited inside the
  // service type they belong to.
  const [seasonsLoading, setSeasonsLoading] = useState(true);
  // Each row: { serviceId, serviceType, seasons: [{ name, startMonth,
  // endMonth, pests }] }.
  const [seasonRows, setSeasonRows] = useState([]);
  // Last-saved rows, used to revert on cancel and to spot service types the
  // admin removed (which need an explicit DELETE).
  const [seasonRowsSnapshot, setSeasonRowsSnapshot] = useState([]);
  // Insects referenced by saved seasons, so labels render before the catalog loads.
  const [seasonInsects, setSeasonInsects] = useState([]);
  // Inline error for the visit windows: { message, details: string[] }.
  const [seasonsError, setSeasonsError] = useState(null);

  // Reps: whose phone numbers may generate the welcome survey.
  const [whitelist, setWhitelist] = useState([]);
  const [whitelistLoading, setWhitelistLoading] = useState(true);
  // Add/edit modal: `whitelistEditing` is the entry being edited, or null when
  // adding a new one; the modal is open whenever `whitelistFormOpen` is true.
  const [whitelistFormOpen, setWhitelistFormOpen] = useState(false);
  const [whitelistEditing, setWhitelistEditing] = useState(null);
  const [whitelistDraft, setWhitelistDraft] = useState(EMPTY_REP);
  const [whitelistSaving, setWhitelistSaving] = useState(false);
  const [whitelistFormError, setWhitelistFormError] = useState("");
  // Delete confirmation modal: the entry pending deletion, or null.
  const [whitelistDeleting, setWhitelistDeleting] = useState(null);
  const [whitelistRemoving, setWhitelistRemoving] = useState(false);

  // Offices this company's welcome survey can be generated for.
  const [offices, setOffices] = useState([]);
  const [officesLoading, setOfficesLoading] = useState(true);
  // Add modal: open whenever `officeFormOpen` is true.
  const [officeFormOpen, setOfficeFormOpen] = useState(false);
  const [officeDraft, setOfficeDraft] = useState(EMPTY_OFFICE);
  const [officeSaving, setOfficeSaving] = useState(false);
  const [officeFormError, setOfficeFormError] = useState("");
  // Rep sync: the office whose sync is in flight, and the office + counts the
  // result modal is showing.
  const [syncingOfficeId, setSyncingOfficeId] = useState(null);
  const [syncedOffice, setSyncedOffice] = useState(null);
  const [syncSummary, setSyncSummary] = useState(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(
        getApiEndpoint("/v1/welcome-survey/settings"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load welcome survey settings");
      }

      const result = await response.json();
      const data = result?.data ?? result;
      const mapped = mapSettings(data);
      setSettings(mapped);
      setContractSnapshot(mapped.contractText);

      // Prefill the Service Settings rows from the saved services (falling back
      // to the legacy pest_coverage shape if `services` is absent).
      const parsed = Array.isArray(data?.services)
        ? parseServices(data.services)
        : parsePestCoverage(data?.pest_coverage);
      setServiceBugs(parsed.serviceBugs);
      setServiceBugsSnapshot(parsed.serviceBugs);
      setCoverageInsects(parsed.insects);
      setCoverageServices(parsed.services);
    } catch (err) {
      setError(err.message || "Failed to load welcome survey settings");
    } finally {
      setLoading(false);
    }
  }, [getVerifiedJWT]);

  // Seasonal windows live behind their own endpoint, so they load alongside
  // the settings rather than as part of that payload.
  const loadSeasons = useCallback(async () => {
    setSeasonsLoading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(
        getApiEndpoint("/v1/welcome-survey/service-seasons"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to load service seasons");

      const result = await response.json();
      const parsed = parseServiceSeasons(result?.data ?? result);
      setSeasonRows(parsed.rows);
      setSeasonRowsSnapshot(parsed.rows);
      setSeasonInsects(parsed.insects);
    } catch (err) {
      setSeasonsError({
        message: err.message || "Failed to load service seasons",
        details: [],
      });
    } finally {
      setSeasonsLoading(false);
    }
  }, [getVerifiedJWT]);

  // Keep the latest toast reporter in a ref so the loaders below can stay
  // stable. `showError` gets a fresh identity every time a toast is shown, so
  // depending on it directly would recreate the loader and refire its mount
  // effect on every toast (e.g. a save success would trigger a spurious reload).
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;

  // The rep list lives behind its own endpoint.
  const loadWhitelist = useCallback(async () => {
    setWhitelistLoading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(getApiEndpoint("/v1/welcome-survey/reps"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (!response.ok) throw new Error("Failed to load reps");

      const result = await response.json();
      const data = result?.data ?? result;
      setWhitelist(Array.isArray(data) ? data : []);
    } catch (err) {
      showErrorRef.current(err.message || "Failed to load reps");
    } finally {
      setWhitelistLoading(false);
    }
  }, [getVerifiedJWT]);

  const loadOffices = useCallback(async () => {
    setOfficesLoading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(getApiEndpoint("/v1/fieldroutes/offices"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (!response.ok) throw new Error("Failed to load offices");

      const result = await response.json();
      const data = result?.data ?? result;
      setOffices(Array.isArray(data) ? data : []);
    } catch (err) {
      showErrorRef.current(err.message || "Failed to load offices");
    } finally {
      setOfficesLoading(false);
    }
  }, [getVerifiedJWT]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    loadSeasons();
  }, [loadSeasons]);

  useEffect(() => {
    loadWhitelist();
  }, [loadWhitelist]);

  useEffect(() => {
    loadOffices();
  }, [loadOffices]);

  // Accent color for the Service Types preview. Decorative, so a failure here
  // leaves the preview on the default theme color rather than erroring.
  useEffect(() => {
    let cancelled = false;

    const loadBranding = async () => {
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) return;
        const response = await fetch(
          getApiEndpoint("/v1/welcome-survey/branding"),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );
        if (!response.ok) return;
        const result = await response.json();
        if (!cancelled) setBranding(result?.data ?? result);
      } catch {
        // Preview styling only — leave the accent unset.
      }
    };

    loadBranding();
    return () => {
      cancelled = true;
    };
  }, [getVerifiedJWT]);

  // Fit the life-size device mock to the panel: scale it down by whichever is
  // tighter, the column's width or the room left under the panel's heading and
  // picker. Never scaled up past 1 — the phone is shown at most life size.
  useEffect(() => {
    const stage = phoneStageRef.current;
    if (!stage) return undefined;

    const panel = stage.closest(".survey-live-preview");

    const measure = () => {
      // Measured as an offset inside the sticky panel rather than from the
      // viewport, so the fit doesn't change as the page scrolls.
      const offsetInPanel = panel
        ? stage.getBoundingClientRect().top - panel.getBoundingClientRect().top
        : 0;
      const room =
        window.innerHeight -
        PHONE_STICKY_TOP -
        offsetInPanel -
        PHONE_STICKY_TOP;
      const fit = Math.min(
        1,
        stage.clientWidth / PHONE_OUTER_WIDTH,
        room / PHONE_OUTER_HEIGHT
      );
      setPhoneScale(Math.max(PHONE_MIN_SCALE, Number.isFinite(fit) ? fit : 1));
    };

    measure();

    // The column's width and the panel's own height (the picker wraps, errors
    // appear) both move the goalposts.
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    if (panel) observer.observe(panel);
    window.addEventListener("resize", measure);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [loading, error]);

  // Load the FieldRoutes option catalog (insects + service types) once. Both
  // the Service Settings and Service Seasons editors pick from it.
  const ensureBugSettings = useCallback(async () => {
    // Only fetch the option data once (or if a previous load failed).
    if (bugSettings) return;
    setBugSettingsLoading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(
        getApiEndpoint("/v1/welcome-survey/bug-settings"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load service settings");
      }

      const result = await response.json();
      setBugSettings(result?.data ?? result);
    } catch (err) {
      showError(err.message || "Failed to load service settings");
    } finally {
      setBugSettingsLoading(false);
    }
  }, [bugSettings, getVerifiedJWT, showError]);

  // Enter edit mode for bug settings and fetch the data needed to edit them.
  const handleEditBugs = async () => {
    setIsEditingBugs(true);
    setBugsDirty(false);
    await ensureBugSettings();
  };

  // Service Bug Settings editing rows.
  const addServiceBug = () => {
    setBugsDirty(true);
    // Open the new row straight away — there's nothing to see in the list yet.
    setSelectedServiceIndex(serviceBugs.length);
    setServiceBugs(prev => [
      ...prev,
      {
        serviceId: null,
        includedBugs: [],
        excludedBugs: [],
        cancellationFee: "",
      },
    ]);
  };
  const updateServiceBug = (index, patch) => {
    setBugsDirty(true);
    setServiceBugs(prev =>
      prev.map((entry, i) => (i === index ? { ...entry, ...patch } : entry))
    );
  };
  const removeServiceBug = index => {
    setBugsDirty(true);
    setSelectedServiceIndex(null);
    setServiceBugs(prev => prev.filter((_, i) => i !== index));
  };

  // Visit windows, edited inside the service type they belong to. Rows are
  // keyed by FieldRoutes service id, and a service gets its row the first time
  // a window is added to it.
  const seasonRowFor = serviceId =>
    serviceId == null
      ? null
      : (seasonRows.find(row => row.serviceId === serviceId) ?? null);

  const seasonsFor = serviceId => seasonRowFor(serviceId)?.seasons ?? [];

  // Apply `updater` to one service's window list, creating the row if this is
  // its first window.
  const updateSeasonsFor = (serviceId, updater) => {
    if (serviceId == null) return;
    setSeasonsError(null);
    setBugsDirty(true);
    setSeasonRows(prev => {
      const index = prev.findIndex(row => row.serviceId === serviceId);
      if (index === -1) {
        return [
          ...prev,
          {
            serviceId,
            serviceType: serviceNameByValue[serviceId] || "",
            seasons: updater([]),
          },
        ];
      }
      return prev.map((row, i) =>
        i === index
          ? {
              ...row,
              // Keep the stored name in step with the catalog, since the
              // survey matches windows to subscriptions by service.
              serviceType: serviceNameByValue[serviceId] || row.serviceType,
              seasons: updater(row.seasons),
            }
          : row
      );
    });
  };

  const addSeason = serviceId =>
    updateSeasonsFor(serviceId, seasons => [
      ...seasons,
      { name: "", startMonth: null, endMonth: null, pests: [] },
    ]);

  const updateSeason = (serviceId, seasonIndex, patch) =>
    updateSeasonsFor(serviceId, seasons =>
      seasons.map((season, i) =>
        i === seasonIndex ? { ...season, ...patch } : season
      )
    );

  const removeSeason = (serviceId, seasonIndex) =>
    updateSeasonsFor(serviceId, seasons =>
      seasons.filter((_, i) => i !== seasonIndex)
    );

  // Dropdown/multiselect option sources: the saved pest coverage (for display)
  // merged with the bug-settings catalog (loaded on edit). Bug-settings entries
  // come last so the fuller catalog (e.g. images) wins for the same id.
  const serviceNameByValue = [
    ...coverageServices,
    ...(bugSettings?.service_types ?? []),
  ].reduce((acc, s) => {
    acc[String(s.fr_service_id)] = s.name;
    return acc;
  }, {});
  const serviceTypeOptions = Object.entries(serviceNameByValue).map(
    ([value, name]) => ({ value, label: `${value} - ${name}` })
  );
  // Reverse lookup: service-type name (lowercased) -> fr_service_id. Season rows
  // are keyed by name, but the seasons save call must send the numeric id.
  const serviceIdByName = [
    ...coverageServices,
    ...(bugSettings?.service_types ?? []),
  ].reduce((acc, s) => {
    if (s?.name) acc[s.name.trim().toLowerCase()] = s.fr_service_id;
    return acc;
  }, {});
  const insectByValue = [
    ...coverageInsects,
    ...seasonInsects,
    ...(bugSettings?.insects ?? []),
  ].reduce((acc, i) => {
    acc[String(i.fr_insect_id)] = i;
    return acc;
  }, {});
  const insectOptions = Object.values(insectByValue).map(insect => ({
    value: String(insect.fr_insect_id),
    label: insect.name,
  }));
  const insectImageByValue = Object.values(insectByValue).reduce((acc, i) => {
    acc[String(i.fr_insect_id)] = i.image_url;
    return acc;
  }, {});
  // Target-pest options: the insect catalog, plus any pest saved without a
  // FieldRoutes id so existing entries stay selected instead of dropping.
  const namedPestOptions = [
    ...new Set(
      seasonRows.flatMap(row =>
        row.seasons.flatMap(season =>
          season.pests.filter(value => value.startsWith(NAMED_PEST_PREFIX))
        )
      )
    ),
  ].map(value => ({
    value,
    label: value.slice(NAMED_PEST_PREFIX.length),
  }));
  const seasonPestOptions = [...insectOptions, ...namedPestOptions];

  // Render each insect option as a circular image with the name beside it.
  const renderInsectOption = ({ option }) => (
    <Group gap="sm" wrap="nowrap">
      <Avatar src={insectImageByValue[option.value]} size={24} radius="xl">
        {option.label?.charAt(0)}
      </Avatar>
      <span>{option.label}</span>
    </Group>
  );

  // Resolve a picker value (insect id, or a name-keyed legacy entry) back to
  // the { name, image_url, fr_insect_id } shape the survey renders.
  const pestItemFor = value => {
    if (value.startsWith(NAMED_PEST_PREFIX)) {
      return {
        name: value.slice(NAMED_PEST_PREFIX.length),
        image_url: null,
        fr_insect_id: null,
      };
    }
    const insect = insectByValue[value];
    return insect
      ? {
          name: insect.name,
          image_url: insect.image_url ?? null,
          fr_insect_id: insect.fr_insect_id ?? null,
        }
      : null;
  };

  // The visit windows currently in the editor for a service, converted into the
  // shape the preview builder expects. Reads the live rows rather than the API
  // so unsaved window edits show up in the preview too. Matched on the service
  // id when there is one, falling back to the name for windows saved before a
  // service had coverage configured.
  const previewSeasonsFor = (serviceTypeName, serviceId = null) => {
    const row =
      (serviceId
        ? seasonRows.find(candidate => candidate.serviceId === serviceId)
        : null) ??
      (serviceTypeName
        ? seasonRows.find(
            candidate =>
              candidate.serviceType &&
              candidate.serviceType.toLowerCase() ===
                serviceTypeName.toLowerCase()
          )
        : null);
    if (!row) return [];
    return row.seasons
      .filter(season => season.startMonth && season.endMonth)
      .map(season => ({
        name: season.name,
        start_month: Number(season.startMonth),
        end_month: Number(season.endMonth),
        target_pests: season.pests.map(pestItemFor).filter(Boolean),
      }));
  };

  const selectedService =
    selectedServiceIndex == null ? null : serviceBugs[selectedServiceIndex];

  // What FieldRoutes has on file for the open service, and the visit windows
  // configured against it.
  const selectedServiceSchedule = selectedService?.serviceId
    ? ((bugSettings?.service_types ?? []).find(
        option => String(option.fr_service_id) === selectedService.serviceId
      ) ?? null)
    : null;
  const selectedServiceSeasons = seasonsFor(selectedService?.serviceId ?? null);

  // Opening a service type in the editor points the preview at it, so the panel
  // shows whatever is being worked on.
  const openServiceId = selectedService?.serviceId ?? null;
  useEffect(() => {
    if (openServiceId) setPreviewServiceValue(openServiceId);
  }, [openServiceId]);

  // Service types the preview can be shown as: everything with coverage
  // configured, plus any service type that only has seasonal windows. The two
  // are configured separately and don't have to line up — a name set up on one
  // side only previews exactly the way the live survey renders it.
  const configuredServiceNames = new Set(
    serviceBugs
      .map(entry => serviceNameByValue[entry.serviceId])
      .filter(Boolean)
      .map(name => name.toLowerCase())
  );
  const previewServiceOptions = [
    ...serviceBugs
      .filter(entry => entry.serviceId)
      .map(entry => ({
        value: entry.serviceId,
        label:
          serviceNameByValue[entry.serviceId] || `Service ${entry.serviceId}`,
      })),
    ...[...new Set(seasonRows.map(row => row.serviceType).filter(Boolean))]
      .filter(name => !configuredServiceNames.has(name.toLowerCase()))
      .map(name => ({ value: `${SEASON_ONLY_PREFIX}${name}`, label: name })),
  ];

  // Fall back to the first option whenever the chosen one goes away (a service
  // removed, or its row not yet given a service).
  const previewValue =
    previewServiceValue &&
    previewServiceOptions.some(option => option.value === previewServiceValue)
      ? previewServiceValue
      : (previewServiceOptions[0]?.value ?? null);

  const previewEntry =
    previewValue && !previewValue.startsWith(SEASON_ONLY_PREFIX)
      ? serviceBugs.find(entry => entry.serviceId === previewValue)
      : null;
  const previewServiceName = previewEntry
    ? serviceNameByValue[previewEntry.serviceId] || ""
    : previewValue
      ? previewValue.slice(SEASON_ONLY_PREFIX.length)
      : "";

  // Open the preview on the step the section being edited feeds: the
  // subscription for service types, the schedule for seasons, the terms for the
  // contract text. Otherwise start at the top of the survey.
  const previewStep = isEditingContract
    ? 3
    : isEditingBugs || selectedServiceIndex != null
      ? 1
      : 0;

  // The customer-facing survey for the chosen service type, built from the
  // in-progress editor state so it tracks edits before they're saved.
  const previewData = buildPreviewData({
    serviceType: previewServiceName,
    includedPests: (previewEntry?.includedBugs ?? [])
      .map(pestItemFor)
      .filter(Boolean),
    notIncludedPests: (previewEntry?.excludedBugs ?? [])
      .map(pestItemFor)
      .filter(Boolean),
    seasons: previewSeasonsFor(previewServiceName, previewEntry?.serviceId),
    contractText: settings.contractText,
    termsText: settings.termsText,
    headerImageUrl: settings.headerImageUrl,
    accentColor: branding?.accent_color,
    cancellationFee:
      previewEntry?.cancellationFee === "" ||
      previewEntry?.cancellationFee == null
        ? null
        : Number(previewEntry.cancellationFee),
  });

  // Save a slice of the email settings. Every key is optional server-side, so
  // each edit sends only what it changed.
  const saveSettingsPatch = async (payload, successMessage) => {
    setSavingEmail(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(
        getApiEndpoint("/v1/welcome-survey/settings"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to save welcome survey settings");
      }

      showSuccess(successMessage);
      return true;
    } catch (err) {
      showError(err.message || "Failed to save welcome survey settings");
      return false;
    } finally {
      setSavingEmail(false);
    }
  };

  // Upload a newly selected image to its dedicated endpoint and reflect the
  // returned URL in the form state. The upload endpoint stores the URL itself,
  // so there's nothing further to save.
  const handleImageUpload = async (field, file) => {
    const config = IMAGE_UPLOADS[field];
    if (!config) return;

    setUploadingField(field);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const formData = new FormData();
      formData.append("image", file);

      const response = await fetch(getApiEndpoint(config.endpoint), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload image");
      }

      const result = await response.json();
      const data = result?.data ?? result;
      const newUrl =
        pickImageUrl(data, config.responseKey) ||
        pickImageUrl(result, config.responseKey);

      if (!newUrl) {
        throw new Error("Upload succeeded but no image URL was returned");
      }

      setSettings(prev => ({ ...prev, [field]: newUrl }));
      showSuccess("Image updated");
    } catch (err) {
      showError(err.message || "Failed to upload image");
    } finally {
      setUploadingField(null);
    }
  };

  // Clear an image: the matching remove_* flag deletes it server-side.
  const handleImageRemove = async field => {
    const config = IMAGE_UPLOADS[field];
    if (!config) return;
    const saved = await saveSettingsPatch(
      { [config.removeFlag]: true },
      "Image removed"
    );
    if (saved) setSettings(prev => ({ ...prev, [field]: "" }));
  };

  // Branch details, edited as a block from the email's "Your Branch" column.
  const handleEditBranch = () => {
    setBranchDraft({
      branchName: settings.branchName,
      branchAddress: settings.branchAddress,
      branchPhone: settings.branchPhone,
      branchEmail: settings.branchEmail,
    });
    setEditingBranch(true);
  };

  const handleSaveBranch = async () => {
    const saved = await saveSettingsPatch(
      {
        welcome_survey_branch_name: branchDraft.branchName,
        welcome_survey_branch_address: branchDraft.branchAddress,
        welcome_survey_branch_phone: branchDraft.branchPhone,
        welcome_survey_branch_email: branchDraft.branchEmail,
      },
      "Branch details saved"
    );
    if (saved) {
      setSettings(prev => ({ ...prev, ...branchDraft }));
      setEditingBranch(false);
    }
  };

  // Manager name, edited from the email's manager column (the photo has its
  // own control on the picture itself).
  const handleEditManager = () => {
    setManagerDraft(settings.managerName);
    setEditingManager(true);
  };

  const handleSaveManager = async () => {
    const saved = await saveSettingsPatch(
      { welcome_survey_manager_name: managerDraft },
      "Manager saved"
    );
    if (saved) {
      setSettings(prev => ({ ...prev, managerName: managerDraft }));
      setEditingManager(false);
    }
  };

  // Hero copy — the headline and paragraph at the top of the email. Saving
  // either one blank puts the template's own default back.
  const handleEditHero = () => {
    setHeroDraft({
      headline: settings.emailHeadline,
      body: settings.emailBody,
    });
    setEditingHero(true);
  };

  const handleSaveHero = async () => {
    const saved = await saveSettingsPatch(
      {
        welcome_survey_email_headline: heroDraft.headline,
        welcome_survey_email_body: heroDraft.body,
      },
      "Email copy saved"
    );
    if (saved) {
      setSettings(prev => ({
        ...prev,
        emailHeadline: heroDraft.headline,
        emailBody: heroDraft.body,
      }));
      setEditingHero(false);
    }
  };

  // Contract terms section (its own dedicated save call).
  const handleEditContract = () => {
    setContractSnapshot(settings.contractText);
    setIsEditingContract(true);
  };

  const handleCancelContract = () => {
    setSettings(prev => ({ ...prev, contractText: contractSnapshot }));
    setIsEditingContract(false);
  };

  const handleSaveContract = async () => {
    setSavingContract(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(
        getApiEndpoint("/v1/welcome-survey/contract-text"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            welcome_survey_contract_text: settings.contractText,
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to save contract terms");
      }

      showSuccess("Contract terms saved");
      setContractSnapshot(settings.contractText);
      setIsEditingContract(false);
    } catch (err) {
      showError(err.message || "Failed to save contract terms");
    } finally {
      setSavingContract(false);
    }
  };

  // Discard unsaved service edits — coverage and visit windows both — and go
  // back to what was last saved.
  const handleCancelBugs = () => {
    setServiceBugs(serviceBugsSnapshot);
    setSeasonRows(seasonRowsSnapshot);
    setIsEditingBugs(false);
    setBugsDirty(false);
    setBugError(null);
    setSeasonsError(null);
  };

  // Save a service type as one thing: its coverage (pest lists + cancellation
  // fee) and its visit windows. Coverage goes up in a single call for every
  // service; windows are replaced one service at a time, and a service whose
  // windows are all gone is deleted outright.
  const handleSaveBugs = async () => {
    if (!bugsDirty) {
      setIsEditingBugs(false);
      return;
    }

    const problems = validateSeasonRows(seasonRows);
    if (problems.length > 0) {
      setSeasonsError({
        message: "Fix these before saving:",
        details: problems,
      });
      return;
    }

    setSaving(true);
    setBugError(null);
    setSeasonsError(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      };

      const response = await fetch(
        getApiEndpoint("/v1/welcome-survey/bug-settings"),
        {
          method: "POST",
          headers,
          body: JSON.stringify(
            toBugPayload(serviceBugs, insectByValue, serviceNameByValue)
          ),
        }
      );
      if (!response.ok) {
        // Surface the validation message and any conflicting bugs inline.
        const data = await response.json().catch(() => ({}));
        setBugError({
          message: data.message || "Failed to save service types",
          conflicts: Array.isArray(data.conflicts) ? data.conflicts : [],
        });
        return;
      }

      // Windows only go up for services that are still configured and still
      // have a window; everything else is removed by name (which also covers a
      // service that was dropped entirely).
      const rows = seasonRows
        .filter(row => row.serviceId && row.seasons.length > 0)
        .map(row => ({
          ...row,
          serviceType: (
            serviceNameByValue[row.serviceId] ||
            row.serviceType ||
            ""
          ).trim(),
        }))
        .filter(row => row.serviceType);

      const keptIds = new Set(rows.map(row => row.serviceId));
      const removedNames = seasonRowsSnapshot
        .filter(row => !row.serviceId || !keptIds.has(row.serviceId))
        .map(row => row.serviceType)
        .filter(Boolean);

      for (const name of removedNames) {
        const removal = await fetch(
          getApiEndpoint(
            `/v1/welcome-survey/service-seasons/${encodeURIComponent(name)}`
          ),
          { method: "DELETE", headers }
        );
        if (!removal.ok) {
          throw new Error(`Failed to remove the visit windows for ${name}`);
        }
      }

      for (const row of rows) {
        const saved = await fetch(
          getApiEndpoint("/v1/welcome-survey/service-seasons"),
          {
            method: "PUT",
            headers,
            body: JSON.stringify(
              toSeasonPayload(row, insectByValue, serviceIdByName)
            ),
          }
        );
        if (!saved.ok) {
          const data = await saved.json().catch(() => ({}));
          throw new Error(
            data.message ||
              `Failed to save the visit windows for ${row.serviceType}`
          );
        }
      }

      showSuccess("Service saved");
      setServiceBugsSnapshot(serviceBugs);
      // Windows with nothing in them were just deleted server-side, so the
      // saved rows are the ones that survived.
      setSeasonRows(rows);
      setSeasonRowsSnapshot(rows);
      setIsEditingBugs(false);
      setBugsDirty(false);
    } catch (err) {
      setSeasonsError({
        message: err.message || "Failed to save the service",
        details: [],
      });
    } finally {
      setSaving(false);
    }
  };

  // Reps: open the add/edit modal (no entry means add).
  const openWhitelistForm = (entry = null) => {
    setWhitelistEditing(entry);
    setWhitelistDraft({
      firstName: entry?.first_name ?? "",
      lastName: entry?.last_name ?? "",
      employeeId: entry?.fr_employee_id ?? "",
      phone: entry?.phone ?? "",
      officeIds: (entry?.offices ?? [])
        .map(office => office.id)
        .filter(id => id != null),
    });
    setWhitelistFormError("");
    setWhitelistFormOpen(true);
  };

  // Toggle one of the offices the rep is assigned to.
  const toggleWhitelistOffice = (officeId, checked) =>
    setWhitelistDraft(prev => {
      const current = prev.officeIds ?? [];
      return {
        ...prev,
        officeIds: checked
          ? [...current, officeId]
          : current.filter(id => id !== officeId),
      };
    });

  const closeWhitelistForm = () => {
    setWhitelistFormOpen(false);
    setWhitelistEditing(null);
    setWhitelistFormError("");
  };

  // Create a new rep or update the one being edited, patching just that row
  // into local state rather than refetching the whole list.
  const handleSaveWhitelist = async () => {
    const firstName = whitelistDraft.firstName.trim();
    const lastName = whitelistDraft.lastName.trim();
    const phone = whitelistDraft.phone.trim();
    const editingId = whitelistEditing?.id;
    // NumberInput leaves an empty field as "", which Number() would read as 0.
    const employeeId =
      whitelistDraft.employeeId === "" || whitelistDraft.employeeId == null
        ? null
        : Number(whitelistDraft.employeeId);
    // The offices toggled on. Both endpoints take the rep's complete set:
    // on an edit, offices left off are unassigned.
    const officeIds = (whitelistDraft.officeIds ?? []).map(Number);

    if (!firstName || !lastName || !phone) {
      setWhitelistFormError("First name, last name and phone are required.");
      return;
    }

    // The employee id ties a new rep back to FieldRoutes; on an edit it's
    // fixed, so the field isn't shown and isn't sent.
    if (editingId == null && !Number.isInteger(employeeId)) {
      setWhitelistFormError("Employee ID is required.");
      return;
    }

    setWhitelistSaving(true);
    setWhitelistFormError("");
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      // Edits address the rep by id in the path; adds post to the collection.
      const response = await fetch(
        getApiEndpoint(
          editingId != null
            ? `/v1/welcome-survey/reps/${editingId}`
            : "/v1/welcome-survey/reps"
        ),
        {
          method: editingId != null ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(
            editingId != null
              ? {
                  first_name: firstName,
                  last_name: lastName,
                  phone,
                  office_ids: officeIds,
                }
              : {
                  first_name: firstName,
                  last_name: lastName,
                  fr_employee_id: employeeId,
                  phone,
                  office_ids: officeIds,
                }
          ),
        }
      );

      if (!response.ok) {
        throw new Error(
          editingId != null ? "Failed to update rep" : "Failed to add rep"
        );
      }

      // The save endpoint returns the full updated list, so use it directly.
      const result = await response.json().catch(() => ({}));
      const data = result?.data ?? result;
      if (Array.isArray(data)) setWhitelist(data);

      showSuccess(editingId != null ? "Rep updated" : "Rep added");
      closeWhitelistForm();
    } catch (err) {
      setWhitelistFormError(err.message || "Failed to save rep");
    } finally {
      setWhitelistSaving(false);
    }
  };

  // Delete the rep pending confirmation. The endpoint returns the full
  // updated list, so use it directly rather than refetching.
  const handleDeleteWhitelist = async () => {
    if (!whitelistDeleting) return;
    setWhitelistRemoving(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(
        getApiEndpoint(`/v1/welcome-survey/reps/${whitelistDeleting.id}`),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to delete rep");

      const result = await response.json().catch(() => ({}));
      const data = result?.data ?? result;
      if (Array.isArray(data)) setWhitelist(data);

      showSuccess("Rep deleted");
      setWhitelistDeleting(null);
    } catch (err) {
      showError(err.message || "Failed to delete rep");
    } finally {
      setWhitelistRemoving(false);
    }
  };

  // Offices: open the add modal, defaulting the company to the signed-in one.
  const openOfficeForm = () => {
    setOfficeDraft(EMPTY_OFFICE);
    setOfficeFormError("");
    setOfficeFormOpen(true);
  };

  const closeOfficeForm = () => {
    setOfficeFormOpen(false);
    setOfficeFormError("");
  };

  const updateOfficeDraft = patch =>
    setOfficeDraft(prev => ({ ...prev, ...patch }));

  // Create an office. Every column is NOT NULL, so all the fields are required.
  const handleSaveOffice = async () => {
    const name = officeDraft.name.trim();
    const subdomain = officeDraft.subdomain.trim();
    const authKey = officeDraft.authKey.trim();
    const authToken = officeDraft.authToken.trim();
    // NumberInput leaves an empty field as "", which Number() would turn into
    // a valid 0 — so blanks are ruled out before converting.
    const toId = value =>
      value === "" || value == null ? null : Number(value);
    const frOfficeId = toId(officeDraft.frOfficeId);

    if (
      !name ||
      !subdomain ||
      !authKey ||
      !authToken ||
      !Number.isInteger(frOfficeId)
    ) {
      setOfficeFormError("All fields are required.");
      return;
    }

    setOfficeSaving(true);
    setOfficeFormError("");
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(getApiEndpoint("/v1/fieldroutes/offices"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          name,
          fr_office_id: frOfficeId,
          subdomain,
          auth_key: authKey,
          auth_token: authToken,
          is_active: true,
          welcome_survey_enabled: true,
        }),
      });

      if (!response.ok) throw new Error("Failed to add office");

      // The endpoint may return the full updated list or just the new row.
      const result = await response.json().catch(() => ({}));
      const data = result?.data ?? result;
      if (Array.isArray(data)) {
        setOffices(data);
      } else if (data && typeof data === "object" && Object.keys(data).length) {
        setOffices(prev => [...prev, data]);
      } else {
        loadOffices();
      }

      showSuccess("Office added");
      closeOfficeForm();
    } catch (err) {
      setOfficeFormError(err.message || "Failed to add office");
    } finally {
      setOfficeSaving(false);
    }
  };

  // Pull the office's reps over from FieldRoutes, then show what the sync did.
  const handleSyncReps = async office => {
    const officeId = office?.id;
    if (officeId == null) return;

    setSyncingOfficeId(officeId);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(
        getApiEndpoint(`/v1/welcome-survey/reps/sync/${officeId}`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to sync reps");

      // The endpoint reports counts rather than the reps themselves.
      const result = await response.json().catch(() => ({}));
      const data = result?.data ?? result;

      setSyncedOffice(office);
      setSyncSummary(data && typeof data === "object" ? data : {});

      // The sync can create reps and office links, so pull the list again.
      loadWhitelist();
    } catch (err) {
      showError(err.message || "Failed to sync reps");
    } finally {
      setSyncingOfficeId(null);
    }
  };

  // Read-only fields render borderless and are not interactive (display-only).
  const readOnlyFieldProps = {
    readOnly: true,
    variant: "unstyled",
    styles: { input: { pointerEvents: "none", cursor: "default" } },
  };

  // Shown on the email's logo band when the company hasn't uploaded one.
  const companyName = user?.company_name ?? user?.company?.name ?? "";

  return (
    <PageContent
      breadcrumbs={[
        { label: "Welcome Survey", path: "/welcome-survey" },
        { label: "Settings" },
      ]}
    >
      {loading ? (
        <Group justify="center" py="xl">
          <Loader size="lg" />
        </Group>
      ) : error ? (
        <Alert
          icon={<IconAlertCircle size={20} />}
          title="Error"
          color="red"
          variant="filled"
        >
          {error}
        </Alert>
      ) : (
        <Grid gutter="lg" align="stretch">
          <Grid.Col span={{ base: 12, lg: 7 }}>
            <Stack gap="lg">
              {/* Inbound phone number */}
              <Paper radius="md" withBorder bg="white" p="lg">
                <Stack gap="md">
                  <Title order={4}>Inbound Phone Number</Title>
                  <TextInput
                    placeholder="+1 (555) 555-5555"
                    value={settings.inboundPhone}
                    {...readOnlyFieldProps}
                  />
                </Stack>
              </Paper>

              {/* Email content — the invitation itself, edited in place */}
              <Paper radius="md" withBorder bg="white" p="lg">
                <Stack gap="md">
                  <UnstyledButton
                    onClick={() => setEmailSectionOpen(open => !open)}
                    aria-expanded={emailSectionOpen}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <div>
                        <Title order={4}>Email Content Settings</Title>
                        <Text size="sm" c="dimmed" mt={4}>
                          The invitation your customers get. Click a pencil to
                          change that part of it.
                        </Text>
                      </div>
                      <IconChevronDown
                        size={20}
                        style={{
                          transition: "transform 150ms ease",
                          transform: emailSectionOpen
                            ? "rotate(180deg)"
                            : "none",
                        }}
                      />
                    </Group>
                  </UnstyledButton>

                  <Collapse in={emailSectionOpen}>
                    <WelcomeSurveyEmailPreview
                      settings={settings}
                      accentColor={branding?.accent_color}
                      companyName={companyName}
                      headline={
                        settings.emailHeadline || branding?.email_headline
                      }
                      bodyText={settings.emailBody || branding?.email_body}
                      uploadingField={uploadingField}
                      onSelectImage={handleImageUpload}
                      onRemoveImage={handleImageRemove}
                      editingHero={editingHero}
                      heroDraft={heroDraft}
                      onHeroDraftChange={patch =>
                        setHeroDraft(prev => ({ ...prev, ...patch }))
                      }
                      onEditHero={handleEditHero}
                      onSaveHero={handleSaveHero}
                      onCancelHero={() => setEditingHero(false)}
                      editingBranch={editingBranch}
                      branchDraft={branchDraft}
                      onBranchDraftChange={patch =>
                        setBranchDraft(prev => ({ ...prev, ...patch }))
                      }
                      onEditBranch={handleEditBranch}
                      onSaveBranch={handleSaveBranch}
                      onCancelBranch={() => setEditingBranch(false)}
                      editingManager={editingManager}
                      managerDraft={managerDraft}
                      onManagerDraftChange={setManagerDraft}
                      onEditManager={handleEditManager}
                      onSaveManager={handleSaveManager}
                      onCancelManager={() => setEditingManager(false)}
                      saving={savingEmail}
                    />
                  </Collapse>
                </Stack>
              </Paper>

              {/* Contract terms */}
              <Paper radius="md" withBorder bg="white" p="lg">
                <Stack gap="md">
                  <Group justify="space-between">
                    <Title order={4}>Contract Terms</Title>
                    {!isEditingContract && !loading && !error && (
                      <Tooltip label="Edit">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="lg"
                          aria-label="Edit contract terms"
                          onClick={handleEditContract}
                        >
                          <IconPencil size={22} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>

                  <ContractTextEditor
                    label="Contract Terms"
                    value={settings.contractText}
                    editable={isEditingContract}
                    onChange={html =>
                      setSettings(prev => ({ ...prev, contractText: html }))
                    }
                  />

                  <Text size="xs" c="dimmed">
                    Tip: insert{" "}
                    <Text span ff="monospace" fw={600} c="dimmed">
                      {"{{CANCELLATION_FEE}}"}
                    </Text>{" "}
                    anywhere in the text to include the service's cancellation
                    fee amount.
                  </Text>

                  {isEditingContract && (
                    <Group justify="flex-end" mt="md">
                      <Button
                        variant="default"
                        onClick={handleCancelContract}
                        disabled={savingContract}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSaveContract}
                        loading={savingContract}
                      >
                        Save
                      </Button>
                    </Group>
                  )}
                </Stack>
              </Paper>

              {/* Services: what each plan covers, and when it's serviced */}
              <Paper radius="md" withBorder bg="white" p="lg">
                <Stack gap="md">
                  <Group justify="space-between" align="flex-start">
                    <div>
                      <Title order={4}>Service Types</Title>
                      <Text size="sm" c="dimmed" mt={4}>
                        What each plan covers. Open one to edit it — the live
                        preview follows whichever one you have open.
                      </Text>
                    </div>
                    {!isEditingBugs && !loading && !error && (
                      <Tooltip label="Edit">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="lg"
                          aria-label="Edit service types"
                          onClick={handleEditBugs}
                        >
                          <IconPencil size={22} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>

                  {bugSettingsLoading || seasonsLoading ? (
                    <Group justify="center" py="md">
                      <Loader size="sm" />
                    </Group>
                  ) : selectedService ? (
                    /* Detail: one service type's fields. */
                    <Stack gap="md">
                      <Group justify="space-between">
                        <Button
                          variant="subtle"
                          size="compact-sm"
                          leftSection={<IconArrowLeft size={14} />}
                          onClick={() => setSelectedServiceIndex(null)}
                        >
                          All service types
                        </Button>
                        {isEditingBugs && (
                          <Button
                            variant="subtle"
                            color="red"
                            size="compact-sm"
                            leftSection={<IconTrash size={14} />}
                            onClick={() =>
                              removeServiceBug(selectedServiceIndex)
                            }
                          >
                            Remove service
                          </Button>
                        )}
                      </Group>

                      <Stack gap="sm">
                        <Select
                          label="Service"
                          placeholder="Select a service"
                          // Only offer services not already configured under
                          // another entry (this one keeps its own selection).
                          data={serviceTypeOptions.filter(
                            option =>
                              option.value === selectedService.serviceId ||
                              !serviceBugs.some(
                                (other, i) =>
                                  i !== selectedServiceIndex &&
                                  other.serviceId === option.value
                              )
                          )}
                          value={selectedService.serviceId}
                          onChange={value =>
                            updateServiceBug(selectedServiceIndex, {
                              serviceId: value,
                            })
                          }
                          disabled={!isEditingBugs}
                          searchable
                        />
                        {selectedServiceSchedule && (
                          <Paper withBorder radius="sm" p="sm" bg="gray.0">
                            <Text size="xs" fw={600} c="dimmed" mb={6}>
                              FROM FIELDROUTES
                            </Text>
                            <Group gap="lg" wrap="wrap">
                              <div>
                                <Text size="xs" c="dimmed">
                                  Schedule
                                </Text>
                                <Text size="sm" fw={600}>
                                  {selectedServiceSchedule.frequency_days
                                    ? `Every ${selectedServiceSchedule.frequency_days} days`
                                    : "—"}
                                </Text>
                              </div>
                              <div>
                                <Text size="xs" c="dimmed">
                                  Treatments a year
                                </Text>
                                <Text size="sm" fw={600}>
                                  {selectedServiceSchedule.visits_per_year ??
                                    "—"}
                                </Text>
                              </div>
                              <div>
                                <Text size="xs" c="dimmed">
                                  Default charge
                                </Text>
                                <Text size="sm" fw={600}>
                                  {selectedServiceSchedule.default_charge !=
                                  null
                                    ? `$${selectedServiceSchedule.default_charge}`
                                    : "—"}
                                </Text>
                              </div>
                            </Group>
                            <Text size="xs" c="dimmed" mt={6}>
                              The customer&apos;s own subscription still decides
                              the visits shown on their survey.
                            </Text>
                          </Paper>
                        )}

                        <NumberInput
                          label="Cancellation fee (optional)"
                          description="Fills {{CANCELLATION_FEE}} in the contract terms."
                          placeholder="0.00"
                          value={selectedService.cancellationFee}
                          onChange={value =>
                            updateServiceBug(selectedServiceIndex, {
                              cancellationFee: value,
                            })
                          }
                          disabled={!isEditingBugs}
                          prefix="$"
                          min={0}
                          decimalScale={2}
                          thousandSeparator=","
                          allowNegative={false}
                          hideControls
                        />
                        <MultiSelect
                          label="Target pests"
                          description="Shown to the customer as what's covered."
                          placeholder="Select bugs"
                          data={insectOptions}
                          value={selectedService.includedBugs}
                          onChange={value =>
                            updateServiceBug(selectedServiceIndex, {
                              includedBugs: value,
                            })
                          }
                          renderOption={renderInsectOption}
                          disabled={!isEditingBugs}
                          searchable
                          clearable
                        />
                        <MultiSelect
                          label="Not included bugs"
                          description="Listed as explicitly outside the plan."
                          placeholder="Select bugs"
                          data={insectOptions}
                          value={selectedService.excludedBugs}
                          onChange={value =>
                            updateServiceBug(selectedServiceIndex, {
                              excludedBugs: value,
                            })
                          }
                          renderOption={renderInsectOption}
                          disabled={!isEditingBugs}
                          searchable
                          clearable
                        />

                        <Divider my="xs" />

                        {/* Visit windows for this service only */}
                        <div>
                          <Text fw={600}>Visit windows</Text>
                          <Text size="sm" c="dimmed" mt={4}>
                            Name the visits in each part of the year and pick
                            the bugs they target. The survey labels every
                            serviced month with the window it falls in.
                          </Text>
                        </div>

                        {selectedServiceSeasons.length === 0 ? (
                          <Text c="dimmed" size="sm">
                            {isEditingBugs
                              ? "No windows yet — add one below."
                              : "No visit windows."}
                          </Text>
                        ) : (
                          <Stack gap="sm">
                            {selectedServiceSeasons.map(
                              (season, seasonIndex) => {
                                const wraps =
                                  season.startMonth &&
                                  season.endMonth &&
                                  Number(season.startMonth) >
                                    Number(season.endMonth);

                                return (
                                  <Paper
                                    key={seasonIndex}
                                    withBorder
                                    radius="sm"
                                    p="sm"
                                    bg="gray.0"
                                  >
                                    <Group justify="space-between" mb="xs">
                                      <Text size="sm" fw={600}>
                                        {season.name ||
                                          `Window ${seasonIndex + 1}`}
                                      </Text>
                                      {isEditingBugs && (
                                        <ActionIcon
                                          variant="subtle"
                                          color="red"
                                          size="sm"
                                          aria-label="Remove window"
                                          onClick={() =>
                                            removeSeason(
                                              selectedService.serviceId,
                                              seasonIndex
                                            )
                                          }
                                        >
                                          <IconTrash size={16} />
                                        </ActionIcon>
                                      )}
                                    </Group>

                                    <Stack gap="xs">
                                      <Group grow align="flex-start">
                                        <Select
                                          label="From"
                                          placeholder="Month"
                                          data={MONTH_OPTIONS}
                                          value={season.startMonth}
                                          onChange={value =>
                                            updateSeason(
                                              selectedService.serviceId,
                                              seasonIndex,
                                              { startMonth: value }
                                            )
                                          }
                                          disabled={!isEditingBugs}
                                        />
                                        <Select
                                          label="To"
                                          placeholder="Month"
                                          data={MONTH_OPTIONS}
                                          value={season.endMonth}
                                          onChange={value =>
                                            updateSeason(
                                              selectedService.serviceId,
                                              seasonIndex,
                                              { endMonth: value }
                                            )
                                          }
                                          disabled={!isEditingBugs}
                                        />
                                      </Group>
                                      {wraps && (
                                        <Text size="xs" c="dimmed">
                                          Runs across the year end:{" "}
                                          {
                                            MONTH_LABELS[
                                              Number(season.startMonth) - 1
                                            ]
                                          }{" "}
                                          through{" "}
                                          {
                                            MONTH_LABELS[
                                              Number(season.endMonth) - 1
                                            ]
                                          }
                                          .
                                        </Text>
                                      )}
                                      <TextInput
                                        label="Visit name"
                                        placeholder="Winter Service"
                                        value={season.name}
                                        maxLength={120}
                                        onChange={event =>
                                          updateSeason(
                                            selectedService.serviceId,
                                            seasonIndex,
                                            { name: event.currentTarget.value }
                                          )
                                        }
                                        disabled={!isEditingBugs}
                                      />
                                      <MultiSelect
                                        label="Target bugs"
                                        placeholder="Select bugs"
                                        data={seasonPestOptions}
                                        value={season.pests}
                                        onChange={value =>
                                          updateSeason(
                                            selectedService.serviceId,
                                            seasonIndex,
                                            { pests: value }
                                          )
                                        }
                                        renderOption={renderInsectOption}
                                        disabled={!isEditingBugs}
                                        searchable
                                        clearable
                                      />
                                    </Stack>
                                  </Paper>
                                );
                              }
                            )}
                          </Stack>
                        )}

                        {isEditingBugs && selectedService.serviceId && (
                          <Button
                            variant="subtle"
                            size="compact-sm"
                            leftSection={<IconPlus size={14} />}
                            onClick={() => addSeason(selectedService.serviceId)}
                            style={{ alignSelf: "flex-start" }}
                          >
                            Add window
                          </Button>
                        )}

                        {seasonsError && (
                          <Alert
                            icon={<IconAlertCircle size={20} />}
                            title="Couldn't save this service"
                            color="red"
                          >
                            <Text size="sm">{seasonsError.message}</Text>
                            {seasonsError.details.length > 0 && (
                              <Stack gap={4} mt="xs">
                                {seasonsError.details.map((detail, i) => (
                                  <Text key={i} size="sm">
                                    {detail}
                                  </Text>
                                ))}
                              </Stack>
                            )}
                          </Alert>
                        )}
                      </Stack>
                    </Stack>
                  ) : serviceBugs.length === 0 ? (
                    <Text c="dimmed" size="sm">
                      {isEditingBugs
                        ? "No service types yet. Add one below."
                        : "No service types."}
                    </Text>
                  ) : (
                    /* List: one row per configured service type. */
                    <Stack gap="xs">
                      {serviceBugs.map((entry, index) => (
                        <Paper key={index} withBorder radius="md">
                          <UnstyledButton
                            onClick={() => setSelectedServiceIndex(index)}
                            p="md"
                            w="100%"
                            aria-label={`Open ${
                              serviceNameByValue[entry.serviceId] ||
                              `service ${index + 1}`
                            }`}
                          >
                            <Group justify="space-between" wrap="nowrap">
                              <div>
                                <Text fw={600}>
                                  {serviceNameByValue[entry.serviceId] ||
                                    `Service ${index + 1}`}
                                </Text>
                                <Group gap="xs" mt={6}>
                                  <Badge variant="light" color="gray">
                                    {entry.includedBugs.length} target
                                    {entry.includedBugs.length === 1 ? "" : "s"}
                                  </Badge>
                                  <Badge variant="light" color="gray">
                                    {entry.excludedBugs.length} not included
                                  </Badge>
                                  {entry.cancellationFee !== "" &&
                                    entry.cancellationFee != null && (
                                      <Badge variant="light" color="gray">
                                        ${entry.cancellationFee} cancellation
                                      </Badge>
                                    )}
                                </Group>
                              </div>
                              <IconChevronRight size={18} />
                            </Group>
                          </UnstyledButton>
                        </Paper>
                      ))}
                    </Stack>
                  )}

                  {bugError && (
                    <Alert
                      icon={<IconAlertCircle size={20} />}
                      title="Couldn't save service types"
                      color="red"
                    >
                      <Text size="sm">{bugError.message}</Text>
                      {bugError.conflicts.length > 0 && (
                        <Stack gap={4} mt="xs">
                          {bugError.conflicts.map((conflict, i) => (
                            <Text key={i} size="sm">
                              <strong>{conflict.service_type}:</strong>{" "}
                              {(conflict.bugs || []).join(", ")}
                            </Text>
                          ))}
                        </Stack>
                      )}
                    </Alert>
                  )}

                  {isEditingBugs && (
                    <>
                      <Button
                        variant="light"
                        leftSection={<IconPlus size={16} />}
                        onClick={addServiceBug}
                        style={{ alignSelf: "flex-start" }}
                      >
                        Add service type
                      </Button>
                      <Group justify="flex-end" mt="md" align="center">
                        <Button
                          variant="default"
                          onClick={handleCancelBugs}
                          disabled={saving}
                        >
                          Cancel
                        </Button>
                        <Button onClick={handleSaveBugs} loading={saving}>
                          Save
                        </Button>
                      </Group>
                    </>
                  )}
                </Stack>
              </Paper>

              {/* Reps */}
              <Paper radius="md" withBorder bg="white" p="lg">
                <Stack gap="md">
                  <Group justify="space-between" align="flex-start">
                    <div>
                      <Title order={4}>Reps</Title>
                      <Text size="sm" c="dimmed" mt={4}>
                        Reps whose phone numbers are allowed to generate the
                        welcome survey.
                      </Text>
                    </div>
                    <Button
                      variant="light"
                      leftSection={<IconPlus size={16} />}
                      onClick={() => openWhitelistForm()}
                    >
                      Add rep
                    </Button>
                  </Group>

                  {whitelistLoading ? (
                    <Group justify="center" py="md">
                      <Loader size="sm" />
                    </Group>
                  ) : whitelist.length === 0 ? (
                    <Text c="dimmed" size="sm">
                      No reps yet. Add one above.
                    </Text>
                  ) : (
                    <Paper withBorder radius="md">
                      {whitelist.map((entry, index) => (
                        <React.Fragment key={entry.id ?? index}>
                          {index > 0 && <Divider />}
                          <Group
                            justify="space-between"
                            wrap="nowrap"
                            px="sm"
                            py="xs"
                          >
                            <Group
                              className="rep-phone-row"
                              gap="xl"
                              wrap="nowrap"
                              style={{ minWidth: 0, flex: 1 }}
                            >
                              <Text
                                className="rep-phone-name"
                                size="sm"
                                fw={600}
                                truncate
                                style={{ width: 160, flexShrink: 0 }}
                              >
                                {repName(entry) || "—"}
                              </Text>
                              <Text size="sm" c="dimmed" truncate>
                                {entry.phone || "—"}
                              </Text>
                              <Text size="sm" c="dimmed" truncate>
                                Employee ID: {entry.fr_employee_id ?? "—"}
                              </Text>
                              {/* The offices the rep is linked to. */}
                              <Group
                                gap={4}
                                wrap="wrap"
                                style={{ minWidth: 0 }}
                              >
                                {(entry.offices ?? []).map(office => (
                                  <Badge
                                    key={office.id ?? office.fr_office_id}
                                    variant="light"
                                    size="sm"
                                  >
                                    {office.name}
                                  </Badge>
                                ))}
                              </Group>
                            </Group>
                            <Group gap={4} wrap="nowrap">
                              <Tooltip label="Edit">
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  size="sm"
                                  aria-label="Edit rep"
                                  onClick={() => openWhitelistForm(entry)}
                                >
                                  <IconPencil size={16} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label="Delete">
                                <ActionIcon
                                  variant="subtle"
                                  color="red"
                                  size="sm"
                                  aria-label="Delete rep"
                                  onClick={() => setWhitelistDeleting(entry)}
                                >
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          </Group>
                        </React.Fragment>
                      ))}
                    </Paper>
                  )}
                </Stack>
              </Paper>

              {/* Offices */}
              <Paper radius="md" withBorder bg="white" p="lg">
                <Stack gap="md">
                  <Group justify="space-between" align="flex-start">
                    <div>
                      <Title order={4}>Offices</Title>
                      <Text size="sm" c="dimmed" mt={4}>
                        Offices the welcome survey pulls customer and plan
                        details from.
                      </Text>
                    </div>
                    <Button
                      variant="light"
                      leftSection={<IconPlus size={16} />}
                      onClick={openOfficeForm}
                    >
                      Add office
                    </Button>
                  </Group>

                  {officesLoading ? (
                    <Group justify="center" py="md">
                      <Loader size="sm" />
                    </Group>
                  ) : offices.length === 0 ? (
                    <Text c="dimmed" size="sm">
                      No offices yet. Add one above.
                    </Text>
                  ) : (
                    <Paper withBorder radius="md">
                      {offices.map((office, index) => (
                        <React.Fragment key={office.id ?? index}>
                          {index > 0 && <Divider />}
                          <Group
                            justify="space-between"
                            wrap="nowrap"
                            px="sm"
                            py="xs"
                          >
                            <Text
                              size="sm"
                              fw={600}
                              truncate
                              style={{ width: 160, flexShrink: 0 }}
                            >
                              {office.name || "—"}
                            </Text>
                            <Group
                              gap="xl"
                              wrap="nowrap"
                              style={{ minWidth: 0, flex: 1 }}
                            >
                              <Text size="sm" c="dimmed" truncate>
                                {office.subdomain || "—"}
                              </Text>
                              <Text size="sm" c="dimmed" truncate>
                                Office ID: {office.fr_office_id ?? "—"}
                              </Text>
                            </Group>
                            <Button
                              size="xs"
                              variant="light"
                              leftSection={<IconRefresh size={14} />}
                              loading={syncingOfficeId === office.id}
                              disabled={
                                syncingOfficeId != null &&
                                syncingOfficeId !== office.id
                              }
                              onClick={() => handleSyncReps(office)}
                            >
                              Sync reps
                            </Button>
                          </Group>
                        </React.Fragment>
                      ))}
                    </Paper>
                  )}
                </Stack>
              </Paper>
            </Stack>
          </Grid.Col>

          {/* Live preview of the survey being configured */}
          <Grid.Col span={{ base: 12, lg: 5 }}>
            <div className="survey-live-preview">
              <Paper radius="md" withBorder bg="white" p="lg">
                <Stack gap="md">
                  <Group
                    justify="space-between"
                    align="flex-start"
                    wrap="nowrap"
                  >
                    <div>
                      <Title order={4}>Live Preview</Title>
                    </div>
                    <Tooltip label="Restart preview">
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="lg"
                        aria-label="Restart preview"
                        onClick={() => setPreviewRunId(id => id + 1)}
                      >
                        <IconRefresh size={20} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>

                  {previewServiceOptions.length > 0 ? (
                    <Select
                      label="Service type"
                      description="Whose coverage and calendar the preview shows"
                      data={previewServiceOptions}
                      value={previewValue}
                      onChange={value => {
                        setPreviewServiceValue(value);
                        setPreviewRunId(id => id + 1);
                      }}
                      allowDeselect={false}
                      searchable
                    />
                  ) : (
                    <Text size="sm" c="dimmed">
                      No service types configured yet — showing the survey with
                      sample plan details only.
                    </Text>
                  )}

                  {/* Customers open the survey from a text, so it's shown on
                      an iPhone 16 laid out at the device's real point size and
                      scaled to fit the panel. */}
                  <Box
                    className="survey-live-preview-stage"
                    ref={phoneStageRef}
                    style={{ height: PHONE_OUTER_HEIGHT * phoneScale }}
                  >
                    <div
                      className="survey-live-preview-phone"
                      style={{ transform: `scale(${phoneScale})` }}
                    >
                      <div
                        className="survey-live-preview-island"
                        aria-hidden="true"
                      />
                      <div className="survey-live-preview-screen">
                        <InfoVerificationPage
                          key={`${previewValue}|${previewStep}|${previewRunId}`}
                          previewData={previewData}
                          previewStep={previewStep}
                        />
                      </div>
                      <div
                        className="survey-live-preview-home"
                        aria-hidden="true"
                      />
                    </div>
                  </Box>
                </Stack>
              </Paper>
            </div>
          </Grid.Col>
        </Grid>
      )}

      {/* Add / edit a rep */}
      <Modal
        opened={whitelistFormOpen}
        onClose={closeWhitelistForm}
        title={whitelistEditing ? "Edit Rep" : "Add Rep"}
        size="md"
      >
        <Stack gap="md">
          {whitelistFormError && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
            >
              {whitelistFormError}
            </Alert>
          )}

          <TextInput
            label="First Name"
            placeholder="Enter first name"
            value={whitelistDraft.firstName}
            onChange={event =>
              setWhitelistDraft(prev => ({
                ...prev,
                firstName: event.currentTarget.value,
              }))
            }
            required
            data-autofocus
          />
          <TextInput
            label="Last Name"
            placeholder="Enter last name"
            value={whitelistDraft.lastName}
            onChange={event =>
              setWhitelistDraft(prev => ({
                ...prev,
                lastName: event.currentTarget.value,
              }))
            }
            required
          />
          {/* Set once, when the rep is first added. */}
          {!whitelistEditing && (
            <NumberInput
              label="Employee ID"
              placeholder="Enter FieldRoutes employee ID"
              value={whitelistDraft.employeeId}
              onChange={value =>
                setWhitelistDraft(prev => ({ ...prev, employeeId: value }))
              }
              allowDecimal={false}
              allowNegative={false}
              required
            />
          )}
          <TextInput
            label="Phone"
            placeholder="15555555555"
            value={whitelistDraft.phone}
            onChange={event =>
              setWhitelistDraft(prev => ({
                ...prev,
                // Digits only, capped at 11 (country code + 10-digit number).
                phone: event.currentTarget.value
                  .replace(/\D/g, "")
                  .slice(0, 11),
              }))
            }
            inputMode="numeric"
            maxLength={11}
            required
          />
          <Input.Wrapper
            label="Offices"
            description="The offices this rep belongs to."
          >
            {offices.length === 0 ? (
              <Text size="sm" c="dimmed" mt={6}>
                No offices yet. Add one first.
              </Text>
            ) : (
              <Stack gap="xs" mt={8}>
                {offices.map(office => (
                  <Switch
                    key={office.id}
                    label={office.name || `Office ${office.id}`}
                    size="sm"
                    checked={(whitelistDraft.officeIds ?? []).includes(
                      office.id
                    )}
                    onChange={event =>
                      toggleWhitelistOffice(
                        office.id,
                        event.currentTarget.checked
                      )
                    }
                  />
                ))}
              </Stack>
            )}
          </Input.Wrapper>

          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={closeWhitelistForm}
              disabled={whitelistSaving}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveWhitelist} loading={whitelistSaving}>
              {whitelistEditing ? "Save" : "Add"}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Confirm deleting a rep */}
      <Modal
        opened={whitelistDeleting != null}
        onClose={() => setWhitelistDeleting(null)}
        title="Delete Rep"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm">
            Delete{" "}
            <Text span fw={600}>
              {repName(whitelistDeleting) || "this rep"}
            </Text>
            {whitelistDeleting?.phone ? ` (${whitelistDeleting.phone})` : ""}?
            This can&apos;t be undone.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setWhitelistDeleting(null)}
              disabled={whitelistRemoving}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDeleteWhitelist}
              loading={whitelistRemoving}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Add an office */}
      <Modal
        opened={officeFormOpen}
        onClose={closeOfficeForm}
        title="Add Office"
        size="md"
      >
        <Stack gap="md">
          {officeFormError && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
            >
              {officeFormError}
            </Alert>
          )}

          <TextInput
            label="Name"
            placeholder="Enter office name"
            value={officeDraft.name}
            onChange={event =>
              updateOfficeDraft({ name: event.currentTarget.value })
            }
            required
            data-autofocus
          />
          <NumberInput
            label="FieldRoutes Office ID"
            placeholder="Enter office ID"
            value={officeDraft.frOfficeId}
            onChange={value => updateOfficeDraft({ frOfficeId: value })}
            allowDecimal={false}
            allowNegative={false}
            required
          />
          <TextInput
            label="Subdomain"
            placeholder="Enter subdomain"
            value={officeDraft.subdomain}
            onChange={event =>
              updateOfficeDraft({ subdomain: event.currentTarget.value })
            }
            maxLength={255}
            required
          />
          <TextInput
            label="Auth Key"
            placeholder="Enter auth key"
            value={officeDraft.authKey}
            onChange={event =>
              updateOfficeDraft({ authKey: event.currentTarget.value })
            }
            required
          />
          <TextInput
            label="Auth Token"
            placeholder="Enter auth token"
            value={officeDraft.authToken}
            onChange={event =>
              updateOfficeDraft({ authToken: event.currentTarget.value })
            }
            required
          />

          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={closeOfficeForm}
              disabled={officeSaving}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveOffice} loading={officeSaving}>
              Add
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* What the last office rep sync did */}
      <Modal
        opened={syncedOffice != null}
        onClose={() => setSyncedOffice(null)}
        title={
          syncedOffice?.name
            ? `Reps Synced — ${syncedOffice.name}`
            : "Reps Synced"
        }
        size="md"
      >
        <Stack gap="md">
          <Paper withBorder radius="md">
            {REP_SYNC_ROWS.map((row, index) => (
              <React.Fragment key={row.key}>
                {index > 0 && <Divider />}
                <Group justify="space-between" wrap="nowrap" px="sm" py="xs">
                  <Text size="sm">{row.label}</Text>
                  <Text size="sm" fw={600}>
                    {syncSummary?.[row.key] ?? 0}
                  </Text>
                </Group>
              </React.Fragment>
            ))}
          </Paper>

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setSyncedOffice(null)}>
              Close
            </Button>
          </Group>
        </Stack>
      </Modal>
    </PageContent>
  );
}

export default WelcomeSurveySettingsPage;
